<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { appTabs } from '@/layout/appTabs'

// The Android app's navigation (App.vue shows it instead of the topbar and sidebar): mail, chat, calendar and
// contacts as far as the user has them, and every other page under "More" (AppMore). It steps aside while the
// keyboard is up, so the field typed in keeps the room.
const store = useStore()
const route = useRoute()

const tabs = computed(() => appTabs(store.getters['auth/user']))
const badge = (tab) => (tab.badge && store.getters['navigation/' + tab.badge]) || 0
// The mail accounts are under "More", with the settings
const active = (tab) => String(route.name || '').startsWith(tab.prefix) && route.name !== 'mail.accounts' && route.name !== 'mail.accounts.detail'
const moreActive = computed(() => !tabs.value.some(active))

const typing = ref(false)
// Fields that bring up the keyboard; a checkbox or a radio button does not
const NO_KEYBOARD = ['button', 'checkbox', 'color', 'file', 'hidden', 'image', 'radio', 'range', 'reset', 'submit']
const editable = (element) => !!element && (element.isContentEditable || element.tagName === 'TEXTAREA'
    || (element.tagName === 'INPUT' && !NO_KEYBOARD.includes(element.type)))
const onFocusIn = (event) => { typing.value = editable(event.target) }
const onFocusOut = () => { setTimeout(() => { typing.value = editable(document.activeElement) }, 50) }
// The views take the bar's room while it is away (--app-tabbar-height, App.vue)
watch(typing, (value) => document.documentElement.classList.toggle('app-typing', value))
onMounted(() => {
    document.addEventListener('focusin', onFocusIn)
    document.addEventListener('focusout', onFocusOut)
})
onBeforeUnmount(() => {
    document.removeEventListener('focusin', onFocusIn)
    document.removeEventListener('focusout', onFocusOut)
    document.documentElement.classList.remove('app-typing')
})
</script>

<template>
    <nav v-show="!typing" class="app-tab-bar">
        <router-link v-for="tab in tabs" :key="tab.route" :to="{ name: tab.route }" class="app-tab" :class="{ active: active(tab) }">
            <span class="app-tab-icon">
                <i :class="tab.icon"></i>
                <span v-if="badge(tab) > 0" class="app-tab-badge">{{ badge(tab) > 99 ? '99+' : badge(tab) }}</span>
            </span>
            <span class="app-tab-label">{{ $t(tab.label) }}</span>
        </router-link>
        <router-link :to="{ name: 'app.more' }" class="app-tab" :class="{ active: moreActive }">
            <span class="app-tab-icon"><i class="pi pi-th-large"></i></span>
            <span class="app-tab-label">{{ $t('core.app_more') }}</span>
        </router-link>
    </nav>
</template>

<style scoped>
.app-tab-bar {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    height: var(--app-tabbar-height);
    display: flex;
    background: var(--surface-card, #fff);
    border-top: 1px solid var(--surface-border, #e2e8f0);
    z-index: 900;
}

.app-tab {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.15rem;
    background: none;
    border: none;
    min-width: 0;
    color: var(--text-color-secondary);
    font-size: 0.72rem;
    text-decoration: none;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
}

.app-tab.active {
    color: var(--primary-color);
    font-weight: 600;
}

.app-tab-icon {
    position: relative;
    font-size: 1.25rem;
    line-height: 1;
}

.app-tab-badge {
    position: absolute;
    top: -0.45rem;
    left: 0.9rem;
    min-width: 1.1rem;
    padding: 0.1rem 0.3rem;
    border-radius: 999px;
    background: var(--red-500, #ef4444);
    color: #fff;
    font-size: 0.65rem;
    font-weight: 600;
    text-align: center;
}
</style>
